import { all, delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import {
  fetchKnmDocumentsByPlanIdDownload,
  fetchKnmPlanDocumentAttachmentDownload,
} from 'APIPackage/KnmModule/services/knmDocumentAttachmentApi';
import { IKnmPlanInfo } from 'APIPackage/PlanListModule/interfaces/IKnmPlanInfo';
import {
  exportPlan,
  fetchPlanById,
  fetchPlanChangeKno,
  fetchPlanHistory,
  fetchPlanInfo,
  fetchPlanSignatureUpload,
  getPlanXmlById,
  updateErknmPlanStatusById,
  updatePlanById,
  updatePlanStatusById,
  updatePlanStatusToConsiderById,
} from 'APIPackage/PlanListModule/services/knmPlanApi';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet, catalogKnmTypes248Get } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import {
  notifyExportError,
  notifyExportSuccess,
  notifyPlanStatusesUpdateError,
  notifyPlanStatusesUpdateSuccess,
} from 'CorePackage/MainModule/sagas/commonNotificationSagas';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { PlanSignatureUploadErrorsEnum } from 'APIPackage/PlanListModule/enums/PlanSignatureUploadErrorsEnum';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { HANDLING_PLAN } from 'CorePackage/MainModule/constants/notices';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { coreSetLoadingProgress } from 'CorePackage/MainModule/actions/coreActions';
import { accountOrganizationsWithDistrictGet } from 'CorePackage/AccountModule/actions/accountActions';
import { KnmHistoryOperationsEnum } from 'APIPackage/KnmModule/enums/KnmHistoryOperationsEnum';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { knmHistoryFieldOperationsMap } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldOperationsMap';
import { KNM_HISTORY_FIELD_TEXT_PREFIX } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldTextPrefix';
import { uuidRegExp } from 'KnmPackage/MainModule/constants/uuidRegExp';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { getUserRoleSysName } from 'AuthPackage/MainModule/selectors/authSelectors';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { fetchKnoOrganisationsIsPrivilegesKno } from 'APIPackage/CatalogsModule/services/knoOrganisationsApi';
import { knmAddVisibleModal } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import {
  dictionariesItemValueGetSuccess,
  dictionariesPositionsItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';

import { planSignSaga, planTestSignSaga } from './planSignSaga';
import {
  planChangeKno,
  planCheckIsPrivilegesKno,
  planDownloadDocumentAttachment,
  planExportPlanById,
  planGetPlanDocuments,
  planGetPlanHistory,
  planInitPage,
  planSetInfo,
  planSetIsPrivilegesKno,
  planSetLoadingDocuments,
  planSetLoadingHistory,
  planSetPlan,
  planSetPlanDocuments,
  planSetPlanHistory,
  planSetPlanHistoryChanges,
  planSetSupervisionTypeErknm,
  planSignPlanById,
  planSignUpload,
  planUpdateErknmPlanStatus,
  planUpdatePlanStatus,
  planUpdatePlanStatusToConsider,
  planSignTestPlanById,
  planUpdate,
  planSetInitialFormValues,
  planExportXmlForSign,
} from '../actions/planActions';
import { PlanActionsEnum } from '../enums/PlanActionsEnum';
import { planHistoryFields } from '../constants/planHistoryFields';
import { IDictionaryVersion } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { getPlanData, getPlanKnoSupervisionUuid } from '../selectors/planSelectors';
import { buildPlanUpdateRequest } from '../common/buildPlanUpdateRequest';
import { IPlanFormValues } from '../interfaces/PlanForm';

const WARNING_KNMS_SIZE = 200;

const commonCatalogsNames: Array<CommonCatalogsNamesEnum> = [
  CommonCatalogsNamesEnum.LIST_TYPES,
  CommonCatalogsNamesEnum.KNM_TYPES_CANCELED,
  CommonCatalogsNamesEnum.USER_PROSECUTORS,
];

function* initCommonCatalogsSaga() {
  yield all([...commonCatalogsNames.map(catalogName => put(catalogGet(catalogName)))]);
  yield put(accountOrganizationsWithDistrictGet());
  yield put(catalogKnmTypes248Get({}));
}

export function* watchPlanSaga() {
  yield takeEvery(PlanActionsEnum.PLAN_INIT_PAGE, planInitPageSaga);
  yield takeEvery(PlanActionsEnum.PLAN_DOWNLOAD_DOCUMENT_ATTACHMENT, planDownloadDocumentAttachmentSaga);
  yield takeEvery(PlanActionsEnum.PLAN_EXPORT_PLAN_BY_ID, exportPlanByIdSaga);
  yield takeEvery(PlanActionsEnum.PLAN_UPDATE_PLAN_STATUS, updatePlanStatusSaga);
  yield takeEvery(PlanActionsEnum.PLAN_UPDATE_ERKNM_PLAN_STATUS, updateErknmPlanStatusSaga);
  yield takeEvery(PlanActionsEnum.PLAN_UPDATE_PLAN_STATUS_TO_CONSIDER, updatePlanStatusToConsiderSaga);
  yield takeEvery(PlanActionsEnum.PLAN_SIGN_PLAN_BY_ID, signPlanSaga);
  yield takeEvery(PlanActionsEnum.PLAN_SIGN_TEST_PLAN_BY_ID, signTestPlanSaga);
  yield takeEvery(PlanActionsEnum.PLAN_SIGN_UPLOAD, planSignUploadSaga);
  yield takeEvery(PlanActionsEnum.PLAN_GET_PLAN_DOCUMENTS, planGetPlanDocumentsSaga);
  yield takeEvery(PlanActionsEnum.PLAN_GET_PLAN_HISTORY, planGetPlanHistorySaga);
  yield takeEvery(PlanActionsEnum.PLAN_CHANGE_KNO, planChangeKnoSaga);
  yield takeEvery(PlanActionsEnum.PLAN_CHECK_IS_PRIVILEGES_KNO, planCheckIsPrivilegesKnoSaga);
  yield takeEvery(PlanActionsEnum.PLAN_UPDATE, updatePlanByIdSaga);
  yield takeEvery(
    [PlanActionsEnum.PLAN_SET_SUPERVISION_TYPE_VALUE, DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS],
    initDictionariesPositions,
  );
  yield takeEvery(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS, planSetValueByOptionsSaga);
  yield takeEvery(PlanActionsEnum.PLAN_EXPORT_XML_FOR_SIGN, exportXmlForSignSaga);
}

function* planSetValueByOptionsSaga(action: ActionType<typeof dictionariesItemValueGetSuccess>) {
  const { dictionaryUUID } = action.payload;
  const state: IRootState = yield select();
  const currentPlanData = getPlanData(state);

  switch (dictionaryUUID) {
    case DictionaryUUIDsEnum.PARTICIPANT_POSITIONS: {
      const signerPosition = currentPlanData?.knmPlanData?.signerPosition;

      const signerPositionValue: IDictionaryPositionSelectOption | undefined = getDictionarySelectOptionWithState<
        typeof dictionaryUUID
      >(state, signerPosition?.dictId, signerPosition?.dictVersionId);

      if (signerPositionValue) {
        yield put(
          planSetInitialFormValues({
            signerPosition: signerPositionValue,
          }),
        );
      }
      break;
    }
    default:
  }
}

function* updatePlanByIdSaga(action: ActionType<typeof planUpdate>) {
  const { id, plan, cb } = action.payload;

  function* funcTry() {
    yield updatePlanById(id, plan);
    cb();
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* planInitPageSaga(action: ActionType<typeof planInitPage>) {
  function* funcTry() {
    const { id } = action.payload;

    yield initCommonCatalogsSaga();
    yield put(planGetPlanDocuments(id));
    yield put(planGetPlanHistory(id));
    const plan: IKnmPlan = yield fetchPlanById(Number(id));

    if (plan.knmPlanData?.supervisionTypeErknm) {
      const dictionaryVersion: IDictionaryVersion = yield fetchDictionaryValue(
        plan.knmPlanData.supervisionTypeErknm.dictId,
        plan.knmPlanData.supervisionTypeErknm.dictVersionId,
      );

      const type = JSON.parse(dictionaryVersion.value);

      yield put(planSetSupervisionTypeErknm(type));
    } else {
      yield put(planSetSupervisionTypeErknm(undefined));
    }

    yield put(planSetPlan(plan));
    const planInfo: IKnmPlanInfo = yield fetchPlanInfo(id);

    yield put(planSetInfo(planInfo));
    yield initDictionariesPositions();
  }

  function* funcCatch(res: IResponseError) {
    if (res?.message?.includes('ACCESS_DENIED_FOR_TEST')) {
      yield put(knmAddVisibleModal(KnmModalsEnum.ACCESS_DENIED_PLAN_FOR_TEST));
    }
  }

  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* initDictionariesPositions() {
  function* funcTry() {
    const state: IRootState = yield select();

    const planSupervisionUuid = getPlanKnoSupervisionUuid(state);

    if (planSupervisionUuid) {
      yield put(dictionariesPositionsItemGet(planSupervisionUuid));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* planGetPlanDocumentsSaga(action: ActionType<typeof planGetPlanDocuments>) {
  function* funcTry() {
    const { id } = action.payload;

    yield put(planSetLoadingDocuments(true));
    const documents: IKnmAttachmentDownload[] = yield fetchKnmDocumentsByPlanIdDownload(Number(id));

    yield put(planSetPlanDocuments(documents));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planSetLoadingDocuments(false));
    },
  });
}

/**
 * Данные подписанта бэк берет из БД, поэтому перед отправкой подписи план
 * нужно сохранить.
 */
function* savePlanSignerData(planId: number, form: IPlanFormValues) {
  const state: IRootState = yield select();
  const plan = getPlanData(state);

  if (plan) {
    yield updatePlanById(planId.toString(), buildPlanUpdateRequest(plan, form));
  }
}

function* planSignUploadSaga(action: ActionType<typeof planSignUpload>) {
  yield sagaWrapper({
    *funcTry() {
      const { planId, document, sign, form } = action.payload;

      // порядок важен: подпись уходит на бэк только после успешного сохранения ФИО и должности
      if (form) {
        yield savePlanSignerData(planId, form);
      }

      yield fetchPlanSignatureUpload(planId, document, sign);
      yield put(planInitPage(planId.toString()));
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.SIGN_UPLOAD_PLAN,
          renderContent: 'Подпись успешно загружена',
        }),
      );
    },
    *funcCatch(res: IResponseError) {
      if (res?.message?.includes(PlanSignatureUploadErrorsEnum.KNM_PLAN_HAS_UNSIGNED_KNM)) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SIGN_UPLOAD_PLAN,
            renderContent: 'Не все КНМ в плане подписаны. Подпишите все КНМ плана и повторите попытку',
          }),
        );
      } else if (res?.message?.includes(PlanSignatureUploadErrorsEnum.KNM_PLAN_XML_NOT_MATCH_UPLOADED)) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SIGN_UPLOAD_PLAN,
            renderContent: 'Загруженный файл отличается от файла для подписания, сформированного в ЕРКНМ',
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SIGN_UPLOAD_PLAN,
            renderContent: res?.message?.length ? res?.message : 'Ошибка загрузки подписи',
          }),
        );
      }
    },
  });
}

function* planGetPlanHistorySaga(action: ActionType<typeof planGetPlanHistory>) {
  function* funcTry() {
    const { id } = action.payload;

    yield put(planSetLoadingHistory(true));
    const history: IKnmHistoryItem[] = yield fetchPlanHistory(Number(id));

    yield planFormedHistoryChanges(history);
    yield put(planSetPlanHistory(history));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planSetLoadingHistory(false));
    },
  });
}

function* planFormedHistoryChanges(knmHistory: IKnmHistoryItem[]) {
  const newKnmHistoryChanges: IKnmHistoryItemChanges[] = [];

  knmHistory?.forEach(knmHistoryItem => {
    const title = `Обновлено ${knmHistoryItem.user?.fullName} (${knmHistoryItem.user?.username}) ${formatDate({
      date: knmHistoryItem.utcTimestamp,
      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      parseFormat: DATE_FORMAT_ISO_FNS,
    })} (МСК)`;
    const historyChangesText: Array<string> = [];

    knmHistoryItem.changes.forEach(changesItem => {
      const newField = changesItem.field
        .split('/')
        .map(item => (uuidRegExp.test(item) || (item !== '' && Number.isInteger(Number(item))) ? '{id}' : item))
        .join('/');

      const field = planHistoryFields[newField];

      if (!field?.active) {
        return;
      }
      const { oldValue, newValue } = changesItem;
      let oldValueText = oldValue;
      let newValueText = newValue;

      if (field.renderFunc) {
        const { renderedOldValue, renderedNewValue } = field.renderFunc(changesItem);

        oldValueText = renderedOldValue;
        newValueText = renderedNewValue;
      }

      let valuesText = '';

      if (changesItem.operation === KnmHistoryOperationsEnum.INSERT) {
        valuesText = `"${newValueText}"`;
      } else if (changesItem.operation === KnmHistoryOperationsEnum.UPDATE) {
        valuesText = `${oldValueText ? `с "${oldValueText}" ` : ''}на "${newValueText}"`;
      }

      const changeText = `${KNM_HISTORY_FIELD_TEXT_PREFIX} "${field.name}" ${
        knmHistoryFieldOperationsMap[changesItem.operation]
      } ${valuesText}`;

      historyChangesText.push(changeText);
    });
    if (historyChangesText.length) {
      newKnmHistoryChanges.push({
        title,
        changesText: historyChangesText,
        eventDate: knmHistoryItem.utcTimestamp,
      });
    }
  });
  yield put(planSetPlanHistoryChanges(newKnmHistoryChanges.reverse()));
}

function* planDownloadDocumentAttachmentSaga(action: ActionType<typeof planDownloadDocumentAttachment>) {
  function* funcTry() {
    const { attachment } = action.payload;

    const attachmentFileData: Blob = yield fetchKnmPlanDocumentAttachmentDownload({ attachment });

    if (attachmentFileData) {
      yield saveFile(attachmentFileData, attachment.fileName || '');
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportPlanByIdSaga(action: ActionType<typeof planExportPlanById>) {
  function* funcTry() {
    const { format, id, isApproved, isConsidered } = action.payload;
    const meta: IExport = yield exportPlan(format, id, isApproved, isConsidered);

    yield notifyExportSuccess(meta.id);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield notifyExportError();
    },
  });
}

function* updatePlanStatusSaga(action: ActionType<typeof planUpdatePlanStatus>) {
  yield sagaWrapper({
    *funcTry() {
      const { id, status } = action.payload;

      yield updatePlanStatusById(id, status);
      yield delay(REFRESH_DELAY);
      yield put(planInitPage(id.toString()));
      yield notifyPlanStatusesUpdateSuccess();
    },
    *funcCatch(res: IResponseError) {
      yield notifyPlanStatusesUpdateError(res.message || '');
    },
  });
}

function* updateErknmPlanStatusSaga(action: ActionType<typeof planUpdateErknmPlanStatus>) {
  const { id, data, knmAmount } = action.payload;

  yield sagaWrapper({
    *funcTry() {
      if (data.newStatus === ErknmPlanStatusesEnum.UNDER_CONSIDERATION && knmAmount > WARNING_KNMS_SIZE) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.PLAN_UPDATE_STATUS,
            renderContent: HANDLING_PLAN,
          }),
        );
      }
      yield updateErknmPlanStatusById(id, data);
      yield delay(REFRESH_DELAY);
      yield put(planInitPage(id.toString()));
      yield notifyPlanStatusesUpdateSuccess();
    },
    *funcCatch(res: IResponseError) {
      yield notifyPlanStatusesUpdateError(res.message || '', data.newStatus);
    },
  });
}

function* updatePlanStatusToConsiderSaga(action: ActionType<typeof planUpdatePlanStatusToConsider>) {
  yield sagaWrapper({
    *funcTry() {
      const { id, data } = action.payload;

      yield updatePlanStatusToConsiderById(id, data);
      yield delay(REFRESH_DELAY);
      yield put(planInitPage(id.toString()));
      yield notifyPlanStatusesUpdateSuccess();
    },
    *funcCatch(res: IResponseError) {
      yield notifyPlanStatusesUpdateError(res.message || '', ErknmPlanStatusesEnum.CONSIDERED);
    },
  });
}

function* signPlanSaga(action: ActionType<typeof planSignPlanById>) {
  const { planId, certificate, errors, form } = action.payload;
  const unload = (e: BeforeUnloadEvent) => {
    e.returnValue = '';

    return '';
  };

  yield sagaWrapper({
    *funcTry() {
      if (planId) {
        yield put(coreSetLoadingProgress(0));
        window.addEventListener('beforeunload', unload);
        yield planSignSaga(planId, certificate, { page: 'plan' }, errors, form);
      }
    },
    *funcCatch() {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SIGN_PLAN,
          renderContent: 'Ошибка подписания плана',
        }),
      );
    },
    *funcFinally() {
      yield put(coreSetLoadingProgress(undefined));
      window.removeEventListener('beforeunload', unload);
    },
  });
}

function* signTestPlanSaga(action: ActionType<typeof planSignTestPlanById>) {
  const { planId } = action.payload;

  yield sagaWrapper({
    *funcTry() {
      if (planId) {
        yield put(coreSetLoadingProgress(0));
        yield planTestSignSaga(planId, { page: 'plan' });
      }
    },
    *funcCatch() {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SIGN_PLAN,
          renderContent: 'Ошибка подписания плана',
        }),
      );
    },
    *funcFinally() {
      yield put(coreSetLoadingProgress(undefined));
    },
  });
}

function* planChangeKnoSaga(action: ActionType<typeof planChangeKno>) {
  yield sagaWrapper({
    *funcTry() {
      const { planId, knoValue, document } = action.payload;

      yield fetchPlanChangeKno(planId, knoValue.id.toString(), document);
      yield delay(REFRESH_DELAY);
      yield put(planInitPage(planId.toString()));
    },
    *funcCatch() {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PLAN_CHANGE_KNO,
          renderContent: 'Ошибка смены КНО',
        }),
      );
    },
  });
}

function* planCheckIsPrivilegesKnoSaga(action: ActionType<typeof planCheckIsPrivilegesKno>) {
  function* funcTry() {
    const roleSysName: UserRoleEnum = yield select(getUserRoleSysName);

    if (
      roleSysName === UserRoleEnum.SUPERVISOR ||
      roleSysName === UserRoleEnum.PROSECUTOR ||
      roleSysName === UserRoleEnum.SUPERIOR_PROSECUTOR
    ) {
      const { ogrn } = action.payload;

      const isPrivilegesKno: boolean = yield fetchKnoOrganisationsIsPrivilegesKno(ogrn);

      yield put(planSetIsPrivilegesKno(isPrivilegesKno));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportXmlForSignSaga(action: ActionType<typeof planExportXmlForSign>) {
  function* funcTry() {
    const { id } = action.payload;

    const planXml: Blob = yield getPlanXmlById(id);

    if (planXml) {
      yield saveFile(planXml, `${id}_план.xml`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield notifyExportError();
    },
  });
}
